/**
 * Strategy preview utilities.
 *
 * Extracted from Strategies.jsx so both the strategy list cards and the
 * StrategyBuilder live-preview panel can share the same logic.
 *
 * Also provides `buildLivePreview` for the builder's real-time preview panel,
 * which is intentionally separate from `buildSchemaV2Payload` (which validates
 * and requires a complete form submission).
 *
 * AI-mode entry point: when AI Assist mode is added, it will call
 * `hydrateFromV2Payload(generatedPayload)` in StrategyBuilder to populate form
 * state, then `buildLivePreview()` will immediately render a preview of the
 * AI-generated strategy before the user saves.
 */

// ─── Indicator tree walker ─────────────────────────────────────────────────────

/** Walk a condition tree and accumulate indicator operands by name. */
export function collectIndicators(node, out = new Map()) {
  if (!node) return out;
  if (node.node_type === 'condition') {
    for (const op of [node.left, node.right]) {
      if (op?.kind === 'series' && op?.source === 'indicator') {
        const n = String(op.name || '').toUpperCase();
        if (n) {
          if (!out.has(n)) out.set(n, []);
          out.get(n).push(op.params || {});
        }
      }
    }
  } else if (node.node_type === 'group') {
    for (const child of (node.children || [])) collectIndicators(child, out);
  }
  return out;
}

function treeContainsLtp(node) {
  if (!node) return false;
  if (node.node_type === 'condition') {
    return [node.left, node.right].some(
      (op) => op?.kind === 'series' && op?.source === 'price' && String(op?.name || '').toLowerCase() === 'ltp'
    );
  }
  if (node.node_type === 'group') {
    return (node.children || []).some(treeContainsLtp);
  }
  return false;
}

function deriveEffectiveTimeframeBadge(cj) {
  const meta = cj?.strategy_meta || {};
  const sections = cj?.sections || {};
  const globalRules = cj?.global_rules || {};

  const hasLtpOperand = ['long_entry', 'long_exit', 'short_entry', 'short_exit']
    .some((key) => sections[key]?.enabled && treeContainsLtp(sections[key]?.tree));

  const hasIntrabarExecution = ['long_entry', 'short_entry']
    .some((key) => {
      const mode = String(sections[key]?.execution?.mode || '').toUpperCase();
      return mode === 'LTP_CROSS' || mode === 'CROSS_AND_CLOSE';
    });

  const triggerMode = String(globalRules.trigger_mode || '').toUpperCase();
  if (triggerMode === 'LIVE_INTRABAR' || hasLtpOperand || hasIntrabarExecution) {
    return 'Intrabar';
  }
  return meta.primary_timeframe || '—';
}

// ─── Rule label ────────────────────────────────────────────────────────────────

/**
 * Returns a short human-readable label for the RULE column.
 * Priority: SUPERTREND > VWAP > EMA/SMA cross > named combos > fallback.
 */
export function deriveRuleLabel(cj) {
  const sections = cj?.sections || {};
  const ind = new Map();
  for (const key of ['long_entry', 'short_entry']) {
    const sec = sections[key];
    if (sec?.enabled && sec.tree) collectIndicators(sec.tree, ind);
  }

  if (ind.size === 0) {
    const anyEnabled = Object.values(sections).some(s => s?.enabled);
    return anyEnabled ? 'Custom' : '—';
  }

  const names = [...ind.keys()];

  if (names.includes('SUPERTREND')) return 'Supertrend';
  if (names.includes('VWAP') && names.length === 1) return 'VWAP';

  if (names.length === 1) {
    const name  = names[0];
    const insts = ind.get(name);
    if ((name === 'EMA' || name === 'SMA') && insts.length >= 2) return `${name} Cross`;
    if (name === 'MACD')  return 'MACD';
    if (name === 'RSI')   return 'RSI';
    if (name === 'ADX')   return 'ADX';
    if (name === 'ATR')   return 'ATR';
    return name.charAt(0) + name.slice(1).toLowerCase();
  }

  if (names.length === 2) {
    if (names.includes('VWAP')) {
      const other = names.find(n => n !== 'VWAP');
      return `VWAP + ${other.charAt(0) + other.slice(1).toLowerCase()}`;
    }
    const a = names[0], b = names[1];
    if ((a === 'EMA' || a === 'SMA') && (b === 'EMA' || b === 'SMA')) return `${a} Cross`;
    const fmt = (n) => n.charAt(0) + n.slice(1).toLowerCase();
    return `${a.charAt(0) + a.slice(1).toLowerCase()} + ${fmt(b)}`;
  }

  return 'Multi-condition';
}

// ─── Secondary strip ───────────────────────────────────────────────────────────

/**
 * Returns the compact secondary strip line.
 * Format: "EMA 9/21 · Long only · Intraday · TGT 1.5% · SL 1%"
 */
export function deriveSecondaryStrip(cj) {
  if (!cj) return null;
  const sections    = cj.sections    || {};
  const globalRules = cj.global_rules || {};
  const risk        = cj.risk        || {};
  const parts       = [];

  // ── 1. Indicator params from entry trees ──────────────────────────────────
  const ind = new Map();
  for (const key of ['long_entry', 'short_entry']) {
    const sec = sections[key];
    if (sec?.enabled && sec.tree) collectIndicators(sec.tree, ind);
  }

  for (const [name, insts] of [...ind.entries()].slice(0, 2)) {
    if ((name === 'EMA' || name === 'SMA') && insts.length >= 2) {
      const lengths = insts.map(p => Number(p.length)).filter(Boolean).sort((a, b) => a - b);
      if (lengths.length >= 2) { parts.push(`${name} ${lengths[0]}/${lengths[1]}`); continue; }
    }
    if ((name === 'EMA' || name === 'SMA') && insts.length === 1) {
      const len = insts[0].length;
      if (len) { parts.push(`${name} ${len}`); continue; }
    }
    if (name === 'RSI' && insts[0]?.length)    { parts.push(`RSI ${insts[0].length}`);  continue; }
    if (name === 'MACD')                        { parts.push('MACD');                    continue; }
    if (name === 'SUPERTREND') {
      const { length, multiplier } = insts[0] || {};
      parts.push(length && multiplier ? `ST ${length}×${multiplier}` : 'Supertrend');
      continue;
    }
  }

  // ── 2. Sides ──────────────────────────────────────────────────────────────
  const allowLong  = globalRules.allow_long  ?? true;
  const allowShort = globalRules.allow_short ?? true;
  if ( allowLong && !allowShort) parts.push('Long only');
  if (!allowLong &&  allowShort) parts.push('Short only');

  // ── 3. Trading mode ───────────────────────────────────────────────────────
  if (globalRules.trading_mode === 'POSITIONAL') parts.push('Positional');
  else if (globalRules.trading_mode === 'INTRADAY') parts.push('Intraday');

  // ── 4. Risk ───────────────────────────────────────────────────────────────
  const rFmt = (r) => `${r.value}${r.type === 'percent' ? '%' : 'pt'}`;
  if (risk.target?.enabled      && risk.target?.value      != null) parts.push(`TGT ${rFmt(risk.target)}`);
  if (risk.stop_loss?.enabled   && risk.stop_loss?.value   != null) parts.push(`SL ${rFmt(risk.stop_loss)}`);
  if (risk.trailing_stop?.enabled && risk.trailing_stop?.value != null) parts.push(`TSL ${rFmt(risk.trailing_stop)}`);

  return parts.length ? parts.join(' · ') : null;
}

// ─── Card summary mapper ───────────────────────────────────────────────────────

/** Maps a backend strategy object to card display fields. */
export function mapStrategyToCardSummary(strategy) {
  // Backend may return v2 payload under config_json or config — check both
  const rawConfig = strategy.config_json || strategy.config || {};
  const cj = (rawConfig.schema_version === '2.0') ? rawConfig : null;

  // Manual strategy: simplified card summary (per-symbol qty/risk live in the watchlist)
  if (cj?.strategy_source === 'MANUAL') {
    const mt = cj.manual_trading || {};
    return {
      symbol:         'Manual',
      segment:        mt.segment || 'Manual',
      timeframe:      'Manual',
      ruleLabel:      'Manual',
      secondaryStrip: [mt.exchange, mt.segment, mt.trade_in, mt.product_type, mt.qty_mode]
        .filter(Boolean).join(' · ') || null,
      instrument:     'MANUAL',
    };
  }

  // External strategy: simplified card summary
  // Phase 2: strategy_source field will be populated from DB once migration 013 is applied.
  // For now, detect external mode from saved config when available.
  if (cj?.strategy_source === 'EXTERNAL') {
    const meta = cj.strategy_meta || {};
    const exec = cj.execution || {};
    const quantityCfg = exec.quantity || {};
    const extSource = cj.external_source_type || 'EXTERNAL';
    const sourceLabels = {
      TRADINGVIEW: 'TradingView',
      CHARTINK: 'Chartink',
      AMIBROKER: 'Amibroker',
      PYTHON: 'Python',
      PYTHON_AMIBROKER: 'Python/Amibroker',
    };
    const quantityMode = String(quantityCfg.mode || '').toUpperCase();
    const quantitySummary = (
      quantityMode === 'FIXED_QTY' ? (quantityCfg.fixed_qty ?? null)
      : quantityMode === 'BY_MARGIN' ? 'By Margin'
      : quantityMode === 'BY_RISK' ? 'By Risk'
      : quantityMode === 'BY_ALERT' ? 'By Alert'
      : null
    );
    const orderType = exec.entry_order?.order_type;
    const productType = exec.product_type;
    return {
      symbol:         meta.symbol       || '—',
      segment:        cj.external_segment || 'External',
      timeframe:      'External',
      stratCode:      meta.strat_code   || null,
      ruleLabel:      sourceLabels[extSource] || extSource,
      secondaryStrip: [
        cj.external_segment,
        cj.external_broker_name,
        orderType,
        productType,
        quantityMode ? quantityMode.replace(/^BY_/, 'By ') : null,
      ].filter(Boolean).join(' · ') || null,
      isOptions:      false,
      instrument:     'EXTERNAL',
      strike:         null,
      lots:           null,
      quantity:       quantitySummary,
      totalLegs:      null,
      v2Risk:         null,
      maxTrades:      null,
      cooldown:       null,
      optSides:       null,
    };
  }
  if (cj) {
    const meta        = cj.strategy_meta || {};
    const exec        = cj.execution     || {};
    const bullishLegs = exec.bullish_signal_legs || [];
    const bearishLegs = exec.bearish_signal_legs || [];
    const firstLeg    = bullishLegs[0] || bearishLegs[0];
    const instrument  = exec.instrument || 'UNDERLYING';
    return {
      symbol:          meta.symbol            || '—',
      timeframe:       deriveEffectiveTimeframeBadge(cj),
      stratCode:       meta.strat_code        || null,
      ruleLabel:       deriveRuleLabel(cj),
      secondaryStrip:  deriveSecondaryStrip(cj),
      isOptions:       instrument === 'OPTIONS',
      instrument,
      strike:          firstLeg?.strike_type  || null,
      lots:            firstLeg?.lots         ?? null,
      quantity:        firstLeg?.quantity     ?? null,
      totalLegs:       bullishLegs.length + bearishLegs.length,
      v2Risk:          null,
      maxTrades:       null,
      cooldown:        null,
      optSides:        null,
    };
  }
  // v1 fallback
  const cfg  = strategy.config || {};
  const opt  = cfg.option || {};
  const risk = cfg.risk   || {};
  return {
    symbol:         cfg.symbol           || '—',
    timeframe:      cfg.timeframe        || '—',
    stratCode:      cfg.strat_code       || null,
    ruleLabel:      cfg.rule?.type === 'custom_conditions'
      ? 'Custom'
      : (cfg.rule?.type?.replace('_breakout', '') || '—'),
    secondaryStrip: null,
    isOptions:      !!(opt.strike || opt.lots != null),
    instrument:     cfg.instrument_type || 'EQ',
    hasFutures:     cfg.strategy_type === 'futures' || cfg.instrument_type === 'FUT',
    strike:         opt.strike || null,
    lots:           opt.lots   ?? null,
    quantity:       cfg.quantity ?? null,
    totalLegs:      null,
    v2Risk:         null,
    maxTrades:      risk.max_trades_per_day ?? null,
    cooldown:       risk.cooldown_minutes   ?? null,
    optSides:       (opt.side_bullish || opt.side_bearish)
      ? `${opt.side_bullish || '—'}/${opt.side_bearish || '—'}`
      : null,
  };
}

// ─── Mapped master-strategy card summary ───────────────────────────────────────

/**
 * Maps a `MappedStrategyResponse` (caller-scoped view of an operator-owned
 * master strategy) to the normalized card shape, plus a `mappedMeta`
 * block used by `MappedStrategyCard` to render mapping-specific chrome.
 *
 * All fields defensively default via `??` so a sparse backend payload renders
 * cleanly rather than throwing.
 */
export function mapMappedStrategyToCardSummary(mapped) {
  const m = mapped || {};
  const cfg = m.master_config_summary || {};
  const execMode = String(m.execution_mode ?? '').toUpperCase() || null;
  // Prefer operator's master-strategy qty_mode (FIXED_QTY / BY_MARGIN / BY_RISK /
  // BY_ALERT) for the mapped card's label. Fallback to mapping-level qty_mode.
  const masterQtyMode = String(cfg.qty_mode ?? '').toUpperCase() || null;
  const mappingQtyMode = String(m.qty_mode ?? '').toUpperCase() || null;
  const qtyMode = masterQtyMode || mappingQtyMode;
  const orderType = m.order_type ?? null;
  const productType = m.product_type ?? null;

  const secondary = [
    cfg.instrument,
    cfg.trading_mode,
    orderType,
    productType,
    qtyMode ? qtyMode.replace(/^BY_/, 'By ') : null,
  ].filter(Boolean).join(' · ') || null;

  return {
    symbol:         cfg.instrument ?? '—',
    segment:        'Mapped',
    timeframe:      'Mapped',
    stratCode:      m.master_strategy_name ?? null,
    ruleLabel:      'Operator',
    secondaryStrip: secondary,
    isOptions:      false,
    instrument:     'MAPPED',
    strike:         null,
    lots:           null,
    quantity:       m.qty_value ?? null,
    totalLegs:      null,
    v2Risk:         null,
    maxTrades:      null,
    cooldown:       null,
    optSides:       null,
    mappedMeta: {
      mappingId:                    m.mapping_id ?? null,
      status:                       m.status ?? 'UNKNOWN',
      operatorName:                 m.operator_name ?? 'Operator',
      masterStrategyId:             m.master_strategy_id ?? null,
      masterStrategyName:           m.master_strategy_name ?? 'Master strategy',
      masterStrategyMode:           String(m.master_strategy_mode ?? '').toUpperCase() || null,
      manualLiveSelectionAvailable: m.manual_live_selection_available === true,
      executionMode:                execMode,
      qtyMode,
      qtyValue:                     m.qty_value ?? null,
      orderType,
      mpp:                          m.mpp ?? null,
      productType,
      childDeploymentId:            m.child_deployment_id ?? null,
      childDeploymentStatus:        m.child_deployment_status ?? 'UNKNOWN',
      childRuntimeActivationState:  m.child_runtime_activation_state ?? 'UNKNOWN',
      childPositionState:           m.child_position_state ?? 'FLAT',
      childLatestStartedAt:         m.child_latest_started_at ?? null,
      schedulerEnabled:             typeof m.child_scheduler_enabled === 'boolean' ? m.child_scheduler_enabled : null,
      startTime:                    m.child_schedule_start_time ?? null,
      endTime:                      m.child_schedule_end_time ?? null,
      daysMask:                     Number.isInteger(m.child_schedule_days_mask) ? m.child_schedule_days_mask : null,
      cancelPendingAtEnd:           typeof m.child_cancel_pending_at_end === 'boolean' ? m.child_cancel_pending_at_end : false,
    },
  };
}

// ─── Live preview builder ──────────────────────────────────────────────────────

/**
 * Builds a partial v2-shaped object from live form state.
 * Used by the builder preview panel — avoids calling buildSchemaV2Payload
 * (which validates and requires a complete, submitted form).
 *
 * sectionTrees is the { long_entry, long_exit, short_entry, short_exit } state
 * object from the builder — trees include _id fields added by attachIds().
 */
export function buildLivePreview(formValues, sectionTrees) {
  const sections = {};
  for (const key of ['long_entry', 'long_exit', 'short_entry', 'short_exit']) {
    sections[key] = {
      enabled: formValues?.sections?.[key]?.enabled ?? false,
      tree:    sectionTrees?.[key] ?? null,
    };
  }
  return {
    schema_version: '2.0',
    strategy_meta: {
      symbol:            formValues?.symbol            || '',
      primary_timeframe: formValues?.primary_timeframe || '',
      strat_code:        formValues?.strat_code        || '',
    },
    sections,
    global_rules: formValues?.global_rules || {},
    risk:         formValues?.risk         || {},
    execution:    { instrument: formValues?.execution?.instrument || 'UNDERLYING' },
  };
}
